import {Injectable} from '@angular/core';
import {AuthService} from '@auth0/auth0-angular';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BehaviorSubject, switchMap} from 'rxjs';
import {UserRoles, roles} from '../shared/utils/roles';
import {IAccessData} from '../interfaces/employee.interface';
import {
  AUTH0_USER_ROLES_KEY,
  AUTH0_USER_UNIT_ID_KEY,
} from '../shared/utils/constants';
import {Departments} from '../enums/departments';
import {CompanyManagementService} from './company-management.service';
import {
  managementDeliveryPosition,
  managementPosition,
} from '../enums/delivery-department-positions';
import {IStatusOfEmployee} from '../interfaces/status-of-employee.interface';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class AccessManagementService {
  public statusOfEmployee = new BehaviorSubject<IStatusOfEmployee | null>(null);
  public allowedUnitsForIncreasing: number[] = []; // units of employees whose salary can be increased by the user
  public userPermissions: string[] = [];
  private _userUnitId!: number;

  public set userUnitId(unitId: number) {
    this._userUnitId = unitId;
  }

  constructor(
    private auth: AuthService,
    companyManagementService: CompanyManagementService
  ) {
    this.auth.user$
      .pipe(
        switchMap(user => {
          if (user === null) {
            this.auth.logout({
              logoutParams: {
                returnTo: document.location.origin,
              },
            });
          }
          this.userPermissions = user![AUTH0_USER_ROLES_KEY];
          this.userUnitId = user![AUTH0_USER_UNIT_ID_KEY];
          return companyManagementService.getCompanyUnits();
        }),
        untilDestroyed(this)
      )
      .subscribe(units => {
        companyManagementService.companyUnits = units;
        if (this.userPermissions.includes(UserRoles.deliveryManager)) {
          this.allowedUnitsForIncreasing = [this._userUnitId];
        }
        if (this.userPermissions.includes(UserRoles.headOfDelivery)) {
          this.allowedUnitsForIncreasing =
            companyManagementService.deliveryUnitIds;
        }
      });
  }

  checkPermissions(allowedPermissions: string[]): boolean {
    for (const permission of this.userPermissions) {
      if (allowedPermissions.includes(permission)) {
        return true;
      }
    }
    return false;
  }

  hasHRDataAccess(accessData: IAccessData): boolean {
    const {employeeData, userData} = accessData;

    if (employeeData.employeeId === userData.employeeId) {
      return false;
    }

    if (this.checkPermissions([UserRoles.headOfHr])) {
      return true;
    }

    if (this.checkPermissions([UserRoles.hrManager])) {
      if (
        !employeeData.isLineManager ||
        employeeData.position.name !== UserRoles.projectManager
      ) {
        return true;
      }
    }

    if (this.checkPermissions([UserRoles.projectManager])) {
      if (employeeData.department.name === Departments.Delivery) {
        return !(
          employeeData.position.name === UserRoles.deliveryManager ||
          employeeData.position.name === UserRoles.headOfDelivery
        );
      }
    }

    if (
      this.checkPermissions(roles.DELIVERY_DEPARTMENT) &&
      employeeData.department.name === Departments.Delivery
    ) {
      return true;
    }

    if (
      this.checkPermissions(roles.ALL_DEPARTMENTS_HEADS) &&
      employeeData.department.name === userData.department.name
    ) {
      return true;
    }

    if (
      this.checkPermissions(roles.LINE_MANAGEMENT) &&
      employeeData.lineManagerId === userData.employeeId
    ) {
      return true;
    }

    return false;
  }

  salaryReadAccess(
    accessData: IAccessData,
    additionalPermissions: string[] = []
  ): boolean {
    const {position, unitId} = accessData.employeeData;

    if (
      !this.checkPermissions([...managementPosition, ...additionalPermissions])
    )
      return false;

    if (
      this.checkPermissions([
        UserRoles.headOfDelivery,
        ...additionalPermissions,
      ])
    )
      return true;

    if (
      this.isDeliveryManagerAccess() &&
      !managementDeliveryPosition.includes(position?.name)
    )
      return position?.name === UserRoles.projectManager
        ? unitId === this._userUnitId
        : true;

    return (
      this.isProjectManagerAccess() &&
      !managementPosition.includes(position?.name)
    );
  }

  salaryCreateAccess(accessData: IAccessData): boolean {
    const {position, unitId} = accessData.employeeData;
    if (!this.checkPermissions(managementPosition)) return false;
    // Remove this condition when all requirements will be discussed for the management sub-department
    if (managementPosition.includes(position?.name)) return false;

    if (this.checkPermissions([UserRoles.headOfDelivery])) return true;

    if (unitId !== this._userUnitId) return false;

    if (
      this.isDeliveryManagerAccess() &&
      !managementDeliveryPosition.includes(position?.name)
    )
      return true;

    return (
      this.isProjectManagerAccess() &&
      !managementPosition.includes(position?.name)
    );
  }

  salaryIncreaseReadAccess(accessData: IAccessData): boolean {
    const {department} = accessData.employeeData;
    const additionalPermissions = [
      UserRoles.ceo,
      UserRoles.accountant,
      UserRoles.headOfAccountant,
    ];

    if (department?.name !== Departments.Delivery) return false;

    return this.salaryReadAccess(accessData, additionalPermissions);
  }

  salaryIncreaseCreateAccess(accessData: IAccessData): boolean {
    const {department} = accessData.employeeData;
    if (department?.name !== Departments.Delivery) return false;

    return this.salaryCreateAccess(accessData);
  }

  salaryRecruiterReadAccess(accessData: IAccessData) {
    const {department} = accessData.employeeData;
    if (department?.name === Departments.Delivery) {
      const additionalPermissions = [
        UserRoles.ceo,
        UserRoles.accountant,
        UserRoles.headOfAccountant,
        ...roles.RECRUITMENT_DEPARTMENT,
      ];
      return this.salaryReadAccess(accessData, additionalPermissions);
    }

    return this.checkPermissions(roles.RECRUITMENT_DEPARTMENT);
  }

  salaryRecruiterCreateAccess(accessData: IAccessData) {
    const {department} = accessData.employeeData;

    if (department?.name === Departments.Delivery)
      return this.isRecruiterAccess() || this.salaryCreateAccess(accessData);

    return this.isRecruiterAccess();
  }

  isProjectManagerAccess(): boolean {
    const projectManager = [UserRoles.projectManager];
    return this.checkPermissions(projectManager);
  }

  isDeliveryManagerAccess(): boolean {
    const deliveryManager = [UserRoles.deliveryManager];
    return this.checkPermissions(deliveryManager);
  }

  isRecruiterAccess(): boolean {
    return this.checkPermissions(roles.RECRUITMENT_DEPARTMENT);
  }
}
